import { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';
import { Program, Track } from '../data/mockData';
import { Calendar, Trash2, ImagePlus, X } from 'lucide-react';
import { toast } from 'sonner';
import { useUploadThumbnailMutation, API_BASE_URL } from '../../store/api';

const TITLE_MAX_LENGTH = 80;
const THUMBNAIL_MAX_SIZE = 25 * 1024 * 1024; // 25MB

interface UpdateProgramDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (program: Omit<Program, 'id' | 'channelId'>, editMode?: 'single' | 'series') => Promise<void>;
  onGoToSchedule: (program: Omit<Program, 'id' | 'channelId'>) => void;
  onDelete?: () => Promise<void>;
  program: Program;
  tracks: Track[];
  channelId: number | string;
}

export function UpdateProgramDrawer({
  isOpen,
  onClose,
  onUpdate,
  onGoToSchedule,
  onDelete,
  program,
  tracks,
  channelId,
}: UpdateProgramDrawerProps) {
  const [uploadThumbnail] = useUploadThumbnailMutation();
  const [showEditModeDialog, setShowEditModeDialog] = useState(false);
  const pendingUpdateRef = useRef<Omit<Program, 'id' | 'channelId'> | null>(null);
  const getApiErrorMessage = (error: unknown, fallback: string) => {
    if (error instanceof Error && error.message.trim().length > 0) {
      return error.message;
    }

    if (error && typeof error === 'object') {
      const maybeError = error as { data?: unknown; status?: number | string };
      if (maybeError.data && typeof maybeError.data === 'object') {
        const maybeData = maybeError.data as { detail?: unknown; message?: unknown };
        if (typeof maybeData.detail === 'string') {
          return maybeData.detail;
        }
        if (typeof maybeData.message === 'string') {
          return maybeData.message;
        }
      }

      if (typeof maybeError.data === 'string' && maybeError.data.trim().length > 0) {
        return maybeError.data;
      }
    }

    return fallback;
  };

  const handleDelete = async () => {
    if (!onDelete) {
      return;
    }

    const shouldDelete = window.confirm('Are you sure you want to delete this program?');
    if (!shouldDelete) {
      return;
    }

    try {
      await onDelete();
      toast.success('Program deleted successfully');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to delete program.'));
    }
  };
  const [title, setTitle] = useState(program.title);
  const [trackId, setTrackId] = useState(program.trackId ? String(program.trackId) : '__none__');
  const [description, setDescription] = useState(program.description || '');
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(program.thumbnail || null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setTitle(program.title);
    setTrackId(program.trackId ? String(program.trackId) : '__none__');
    setDescription(program.description || '');
    setThumbnailPreview(program.thumbnail || null);
  }, [program]);

  const handleUpdate = async () => {
    if (!title.trim()) {
      toast.error('Program title is required');
      return;
    }

    const resolvedTrackId = trackId === '__none__' ? null : trackId;

    const updateData: Omit<Program, 'id' | 'channelId'> = {
      title,
      trackId: resolvedTrackId,
      startDateTime: program.startDateTime,
      endDateTime: program.endDateTime,
      scheduleType: program.scheduleType,
      recurrence: program.recurrence,
      description: description || null,
      thumbnail: thumbnailPreview || null,
    };

    // If recurring with a series, ask user whether to edit single or all
    if (program.scheduleType === 'recurring' && program.seriesId) {
      pendingUpdateRef.current = updateData;
      setShowEditModeDialog(true);
      return;
    }

    try {
      await onUpdate(updateData, 'single');
      toast.success('Program updated successfully');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update program.'));
    }
  };

  const handleEditModeConfirm = async (mode: 'single' | 'series') => {
    setShowEditModeDialog(false);
    if (!pendingUpdateRef.current) return;
    try {
      await onUpdate(pendingUpdateRef.current, mode);
      toast.success(
        mode === 'series'
          ? 'All events in series updated'
          : 'This event updated'
      );
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to update program.'));
    }
    pendingUpdateRef.current = null;
  };

  const handleGoToSchedule = () => {
    if (!title.trim()) {
      toast.error('Program title is required');
      return;
    }

    const resolvedTrackId = trackId === '__none__' ? null : trackId;

    onGoToSchedule({
      title,
      trackId: resolvedTrackId,
      startDateTime: program.startDateTime,
      endDateTime: program.endDateTime,
      scheduleType: program.scheduleType,
      recurrence: program.recurrence,
      description: description || null,
      thumbnail: thumbnailPreview || null,
    });
  };

  const handleThumbnailUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      event.target.value = '';
      return;
    }
    if (file.size > THUMBNAIL_MAX_SIZE) {
      toast.error('Thumbnail must be less than 25 MB');
      event.target.value = '';
      return;
    }
    try {
      const result = await uploadThumbnail({ channelId, file }).unwrap();
      setThumbnailPreview(result.url);
    } catch {
      toast.error('Failed to upload thumbnail');
    }
    event.target.value = '';
  };

  return (
    <>
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl bg-gray-900 border-gray-800 text-white overflow-y-auto p-0"
      >
        <SheetHeader className="px-6 sm:px-8 pt-6 sm:pt-8 pb-5 sm:pb-6 border-b border-gray-800">
          <SheetTitle className="text-2xl font-bold text-white">Update Program</SheetTitle>
          <SheetDescription className="text-gray-400 mt-2">
            Edit program details
          </SheetDescription>
        </SheetHeader>

        <div className="px-6 sm:px-8 py-6 sm:py-8 space-y-6">
          {/* Program Title */}
          <div className="space-y-2">
            <Label htmlFor="title" className="text-sm font-semibold text-white">
              Program Title <span className="text-red-500">*</span>
            </Label>
            <Input
              id="title"
              placeholder="Enter program title"
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, TITLE_MAX_LENGTH))}
              maxLength={TITLE_MAX_LENGTH}
              className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 h-11"
            />
            <p className={`text-xs ${title.length >= TITLE_MAX_LENGTH ? 'text-amber-400' : 'text-gray-500'}`}>
              {title.length}/{TITLE_MAX_LENGTH} characters
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-sm font-semibold text-white">
              Description
            </Label>
            <Textarea
              id="description"
              placeholder="Enter program description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={2000}
              className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 min-h-[100px] resize-none"
            />
            <p className="text-xs text-gray-500">{description.length}/2000 characters</p>
          </div>

          {/* Select Track File (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="track" className="text-sm font-semibold text-white">
              Track File <span className="text-gray-500 font-normal">(optional)</span>
            </Label>
            <Select value={trackId} onValueChange={setTrackId}>
              <SelectTrigger
                id="track"
                className={`bg-gray-800 border-gray-700 text-white h-11`}
              >
                <SelectValue placeholder="None (no track)" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-800">
                <SelectItem value="__none__" className="text-gray-400 focus:bg-gray-800 focus:text-gray-300">
                  None
                </SelectItem>
                {tracks.map((track) => (
                  <SelectItem
                    key={track.id}
                    value={String(track.id)}
                    className="text-white focus:bg-gray-800"
                  >
                    {track.fileName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Thumbnail */}
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-white">
              Thumbnail <span className="text-gray-500 font-normal">(optional)</span>
            </Label>
            {thumbnailPreview ? (
              <div className="relative inline-block">
                <img
                  src={thumbnailPreview.startsWith('/') ? `${API_BASE_URL}${thumbnailPreview}` : thumbnailPreview}
                  alt="Thumbnail preview"
                  className="w-32 h-32 rounded-lg object-cover border border-gray-700"
                />
                <button
                  type="button"
                  onClick={() => setThumbnailPreview(null)}
                  className="absolute -top-2 -right-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-1 transition-colors"
                >
                  <X className="size-3" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => thumbnailInputRef.current?.click()}
                className="flex items-center gap-3 w-full p-4 rounded-lg border border-dashed border-gray-700 hover:border-gray-500 bg-gray-800/50 hover:bg-gray-800 transition-colors text-gray-400 hover:text-gray-300"
              >
                <ImagePlus className="size-5" />
                <span className="text-sm">Upload thumbnail image</span>
              </button>
            )}
            <input
              ref={thumbnailInputRef}
              type="file"
              accept="image/*"
              onChange={handleThumbnailUpload}
              className="hidden"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-gray-900 border-t border-gray-800 px-4 sm:px-8 py-4 sm:py-5">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            {onDelete && (
              <Button
                variant="destructive"
                onClick={handleDelete}
                className="gap-2"
              >
                <Trash2 className="size-4" />
                Delete
              </Button>
            )}
            <div className="flex-1 flex flex-col sm:flex-row gap-2 sm:gap-3">
              <Button
                onClick={handleGoToSchedule}
                disabled={!title.trim()}
                className="flex-1 gap-2 bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Calendar className="size-4" />
                Go to Schedule
              </Button>
              <Button
                onClick={handleUpdate}
                disabled={!title.trim()}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                Update
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>

    {/* Edit Mode Dialog for recurring programs */}
    {showEditModeDialog && (
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm"
        onClick={() => setShowEditModeDialog(false)}
      >
        <div
          className="bg-gray-800 border border-gray-600 rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 className="text-lg font-bold text-white mb-2">Edit Recurring Event</h3>
          <p className="text-sm text-gray-400 mb-5">
            This program is part of a recurring series. How would you like to apply your changes?
          </p>
          <div className="space-y-3">
            <Button
              onClick={() => handleEditModeConfirm('single')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              This event only
            </Button>
            <Button
              onClick={() => handleEditModeConfirm('series')}
              variant="outline"
              className="w-full border-gray-600 text-white hover:bg-gray-700"
            >
              All events in this series
            </Button>
            <Button
              onClick={() => setShowEditModeDialog(false)}
              variant="ghost"
              className="w-full text-gray-400 hover:text-white hover:bg-gray-700"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    )}
  </>
  );
}
